import { LogoLink } from "@circle-react/components/Spaces/SidebarV2/LogoTopSection/LogoLink";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  getCommunityLogoUrlAndType,
  isPromoCodeEnabled,
  isSubscriptionExpired,
} from "@circle-react/helpers/communityHelpers";
import { useIsAppShellAdminAreaEnabled } from "@circle-react/hooks/useIsAppShellAdminAreaEnabled";
import { useIsIframeEmbed } from "@circle-react/hooks/useIsIframeEmbed";
import { useMatchAnyRoute } from "@circle-react/hooks/useMatchAnyRoute";
import { useTailwindLgScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useBackendProps } from "@circle-react/providers/AppContext";
import { useThemeContext } from "@circle-react/providers/Theme";
import { useLocation } from "@tanstack/react-router";

import {
  LOGGED_IN,
  LOGGED_OUT,
} from "@/react/components/SettingsApp/Navigation/AddLink/constants";
import { useRouteHelpers } from "@/react/routes/Main/useRouteHelpers";

import { HEADER_V2_1_DISABLED_ROUTES } from "./constants";
import { DropdownWrapper } from "./DropdownWrapper";
import { HeaderWrapper } from "./HeaderWrapper";
import { HeaderWrapperV3 } from "./HeaderWrapperV3";
import { MainCommunityDropdown } from "./MainCommunityDropdown";
import { NavigationBar } from "./NavigationBar";
import { RightActionBlock } from "./RightActionBlock";
import { SettingsSearchBar } from "./SettingsSearchBar";
import { useSettingsHeader } from "./useSettingsHeader";

export interface HeaderV3Props {
  isRenderedOutsideSpace?: boolean;
}

export const HeaderV3 = ({ isRenderedOutsideSpace }: HeaderV3Props) => {
  const { currentCommunity, currentCommunityMember } = usePunditUserContext();
  const isLgScreen = useTailwindLgScreenMediaQuery();
  const isIframeEmbed = useIsIframeEmbed();
  const { isDarkAppearance } = useThemeContext();
  const { url, type } = getCommunityLogoUrlAndType(
    currentCommunity,
    isDarkAppearance,
  );
  const { minimal_layout: isMinimalLayout } = useBackendProps();
  const audience = !currentCommunityMember ? LOGGED_OUT : LOGGED_IN;

  const matchAnyRoute = useMatchAnyRoute();
  const isHeaderDisabledOnRoute = matchAnyRoute(HEADER_V2_1_DISABLED_ROUTES);
  const { isDiscoverPage } = useRouteHelpers();
  const isEoyRecapRoute = useLocation({
    select: location => location.pathname === "/settings/recap-2025",
  });
  const isHeaderDisabled = !isLgScreen || isIframeEmbed;
  const {
    isSettingsRoute,
    isReactFlowWorkflowsRoute,
    isBotBuilderEditorRoute,
  } = useSettingsHeader();
  const hasSubscriptionExpired = isSubscriptionExpired(currentCommunity);
  const isPromoCodeEnabledForCommunity = isPromoCodeEnabled(currentCommunity);

  const isAppShellAdminAreaEnabled = useIsAppShellAdminAreaEnabled();

  const redirectToHostLocation = () => {
    window.location.href = window.location.origin;
  };

  if (
    isReactFlowWorkflowsRoute ||
    isBotBuilderEditorRoute ||
    isHeaderDisabled ||
    hasSubscriptionExpired ||
    isPromoCodeEnabledForCommunity ||
    isHeaderDisabledOnRoute?.isExact ||
    isDiscoverPage ||
    isEoyRecapRoute
  ) {
    return null;
  }

  if (isAppShellAdminAreaEnabled && isSettingsRoute) {
    return null;
  }

  if (isMinimalLayout) {
    return (
      <HeaderWrapper isRenderedOutsideSpace>
        <DropdownWrapper>
          <LogoLink
            logoUrl={url ?? ""}
            logoType={type}
            communityName={currentCommunity?.name ?? ""}
            onLogoClick={redirectToHostLocation}
          />
        </DropdownWrapper>
        <RightActionBlock isRenderedOutsideSpace={isRenderedOutsideSpace} />
      </HeaderWrapper>
    );
  }

  if (isSettingsRoute) {
    return (
      <HeaderWrapperV3>
        <MainCommunityDropdown />
        <SettingsSearchBar />
        <RightActionBlock />
      </HeaderWrapperV3>
    );
  }

  return (
    <HeaderWrapperV3>
      <MainCommunityDropdown />
      <NavigationBar audience={audience} />
      <RightActionBlock />
    </HeaderWrapperV3>
  );
};
